import { cap } from '../utils/format';
import React, { useState, useEffect } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import { RefreshCw, TrendingDown, AlertTriangle, BarChart2, Users, X, Ban, BookOpen, Package, FileText, Shield, Wrench, Printer } from 'lucide-react';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import Badge from '../components/Badge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { fmtLKR } from '../utils/assetUtils';

const TT_COLORS = ['#60a5fa','#34d399','#fbbf24','#f87171','#c084fc','#22d3ee','#fb923c','#f472b6'];

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'linear-gradient(135deg,#0d1528,#080f1e)',
      border: '1px solid rgba(59,130,246,0.4)',
      borderRadius: 12, padding: '10px 14px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      minWidth: 140,
    }}>
      {label && <div style={{color:'#64748b',fontSize:11,marginBottom:6,fontWeight:600}}>{label}</div>}
      {payload.map((p, i) => {
        const c = p.color || p.fill || TT_COLORS[i % TT_COLORS.length];
        const v = typeof p.value === 'number' && p.value > 9999
          ? `LKR ${p.value.toLocaleString()}` : p.value;
        return (
          <div key={i} style={{display:'flex',alignItems:'center',gap:7,marginBottom:i<payload.length-1?4:0}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:c,flexShrink:0,boxShadow:`0 0 5px ${c}`}} />
            <span style={{color:'#64748b',fontSize:12}}>{p.name}:</span>
            <span style={{color:'#f1f5f9',fontSize:12,fontWeight:700}}>{v}</span>
          </div>
        );
      })}
    </div>
  );
};

const ax = { fill: '#1e3a5f', fontSize: 10 };
const GRID = { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.04)' };
const PIE_C = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];

const Card = ({ children, style }) => (
  <div className="glass glow-border rounded-2xl p-5" style={style}>{children}</div>
);
const STitle = ({ children, color='#94a3b8' }) => (
  <div className="text-sm font-semibold mb-4" style={{color}}>{children}</div>
);

const fmtGainLoss = v => {
  if (v == null) return '—';
  const n = parseFloat(v);
  return <span style={{color: n >= 0 ? '#34d399' : '#f87171', fontWeight: 700}}>{n >= 0 ? '+' : ''}{fmtLKR(n)} {n >= 0 ? '(Gain)' : '(Loss)'}</span>;
};

// Tabs visible to each role:
// admin       — all tabs
// it_staff    — EOL, Top Holders, Handover History, Depreciation, Accessories, Toner, Operations
// director    — depreciation, eol, utilization, wdv, disposal, acc_disposal, accessories, licenses, insurance
// finance     — depreciation, eol, utilization, wdv, disposal, acc_disposal, accessories, licenses, insurance
const DIRECTOR_TABS  = ['depreciation','eol','utilization','wdv','disposal','acc_disposal','accessories','licenses','insurance_rep'];
const IT_STAFF_TABS  = ['eol','holders','handovers','depreciation','accessories','toner','operations'];

export default function Reports() {
  const { isFinance, isDirector, isAdmin, isIT } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [dep, setDep] = useState([]);
  const [sum, setSummary] = useState(null);
  const [tickets, setTickets] = useState({ tickets: [], summary: {} });
  const [wdvData, setWdvData] = useState([]);
  const [disposal, setDisposal] = useState({ detail: [], summary: null, byType: [] });
  const [accDisposal, setAccDisposal] = useState([]);
  const [accReport, setAccReport] = useState(null);
  const [tonerReport, setTonerReport] = useState(null);
  const [licenseReport, setLicenseReport] = useState(null);
  const [insuranceReport, setInsuranceReport] = useState(null);
  const [opsReport, setOpsReport] = useState(null);
  const [eolReport, setEolReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('depreciation');
  const [filterCompany, setFilterCompany] = useState(null);
  const [filterDept, setFilterDept] = useState(null);

  const [popup, setPopup] = useState(null);
  const [popupData, setPopupData] = useState([]); const [popupLoading, setPopupLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ticketStatus, setTicketStatus] = useState('');
  const [ticketCategory, setTicketCategory] = useState('');

  const openAssetPopup = (title, params) => {
    setPopup({ title, params });
    setPopupData([]); setPopupLoading(true);
    api.get('/assets', { params: { ...params, limit: 100, from: dateFrom||undefined, to: dateTo||undefined } })
      .then(r => setPopupData((r.data.data || []).map(a => ({
        asset_number: a.asset_number, brand: a.brand, model: a.model,
        category: a.category, status: a.status, purchased_date: a.purchased_date,
        holder_name: a.currentHolder?.full_name, department: a.Department?.name,
        depreciation_status: a.depreciation_status
      }))))
      .catch(() => {})
      .finally(() => setPopupLoading(false));
  };

  const load = async () => {
    setLoading(true);
    const dr = {
      from: dateFrom||undefined, to: dateTo||undefined,
      company_id: Array.isArray(filterCompany) ? (filterCompany.length ? filterCompany.join(',') : undefined) : (filterCompany || undefined),
      department_id: filterDept||undefined
    };
    try {
      if (isFinance || isDirector) {
        // Asset tabs only — no ticket report needed
        const [d, s, wdv, disp, accDisp, accRep, licRep, insRep, eolRep] = await Promise.all([
          api.get('/reports/depreciation', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/summary', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/wdv', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/disposal', { params: dr }).then(r => r.data).catch(() => ({ detail:[], summary:null, byType:[] })),
          api.get('/reports/accessory-disposal', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/accessories', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/licenses', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/insurance', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/eol', { params: dr }).then(r => r.data).catch(() => null),
        ]);
        setDep(d || []); setSummary(s);
        setWdvData(wdv); setDisposal(disp); setAccDisposal(accDisp);
        setAccReport(accRep); setLicenseReport(licRep); setInsuranceReport(insRep);
        setEolReport(eolRep);
      } else {
        // admin / it_staff / junior_it — full report suite
        const [d, s, t, wdv, disp, accDisp, accRep, tonerRep, licRep, insRep, opsRep, eolRep] = await Promise.all([
          api.get('/reports/depreciation', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/summary', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/tickets', { params: { ...dr, status: ticketStatus||undefined, category: ticketCategory||undefined } }).then(r => r.data).catch(() => ({ tickets:[], summary:{} })),
          api.get('/reports/wdv', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/disposal', { params: dr }).then(r => r.data).catch(() => ({ detail:[], summary:null, byType:[] })),
          api.get('/reports/accessory-disposal', { params: dr }).then(r => r.data).catch(() => []),
          api.get('/reports/accessories', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/toner', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/licenses', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/insurance', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/operations', { params: dr }).then(r => r.data).catch(() => null),
          api.get('/reports/eol', { params: dr }).then(r => r.data).catch(() => null),
        ]);
        setDep(d || []); setSummary(s); setTickets(t || { tickets:[], summary:{} });
        setWdvData(wdv); setDisposal(disp); setAccDisposal(accDisp);
        setAccReport(accRep); setTonerReport(tonerRep); setLicenseReport(licRep);
        setInsuranceReport(insRep); setOpsReport(opsRep); setEolReport(eolRep);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const allTabs = [
    ['depreciation', 'Depreciation', <TrendingDown size={13}/>],
    ['eol', 'EOL Analysis', <AlertTriangle size={13}/>],
    ['utilization', 'Asset Utilization', <BarChart2 size={13}/>],
    ['wdv', 'WDV Schedule', <BookOpen size={13}/>],
    ['disposal', 'Asset Disposal', <Ban size={13}/>],
    ['acc_disposal', 'Acc. Disposal', <Package size={13}/>],
    ['tickets', 'IT Tickets', <BarChart2 size={13}/>],
    ['holders', 'Top Holders', <Users size={13}/>],
    ['handovers', 'Handover History', <BarChart2 size={13}/>],
    ['accessories', 'Accessories', <Package size={13}/>],
    ['toner', 'Toner', <FileText size={13}/>],
    ['licenses', 'Licenses', <FileText size={13}/>],
    ['insurance_rep', 'Insurance', <Shield size={13}/>],
    ['operations', 'Operations', <Wrench size={13}/>],
  ];
  const tabs = isAdmin
    ? allTabs
    : (isFinance || isDirector)
      ? allTabs.filter(([key]) => DIRECTOR_TABS.includes(key))
      : isIT
        ? allTabs.filter(([key]) => IT_STAFF_TABS.includes(key))
        : allTabs;

  const eolAssets  = eolReport?.assets || [];
  const eolSummary = eolReport?.summary || { count: 0, allocated: 0, total_cost: 0, total_wdv: 0 };

  // Open the printable EOL report in a new tab. The window must be opened
  // synchronously inside the click handler — opening it after an await is
  // blocked by every popup blocker (Session 19).
  const printEol = () => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set('from', dateFrom);
    if (dateTo) qs.set('to', dateTo);
    const co = Array.isArray(filterCompany) ? filterCompany.join(',') : (filterCompany || '');
    if (co) qs.set('company_id', co);
    if (filterDept) qs.set('department_id', filterDept);
    qs.set('token', localStorage.getItem('token') || '');
    window.open(`/api/reports/eol/print?${qs.toString()}`, '_blank');
  };

  const depData = dep.map(d => ({
    name: d.category?.replace('_',' '),
    Active: +d.active || 0,
    Deprecated: +d.depreciated || 0,
    EOL: +d.eol || 0,
  }));

  const totalDep = dep.reduce((s,d) => s + (+d.depreciated||0), 0);
  const totalActive = dep.reduce((s,d) => s + (+d.active||0), 0);
  const totalEOL = dep.reduce((s,d) => s + (+d.eol||0), 0);
  const totalAssets = totalDep + totalActive + totalEOL + dep.reduce((s,d) => s+(+d.total||0) - (+d.active||0) - (+d.depreciated||0) - (+d.eol||0), 0);

  const statusPie = [
    { name: 'Active', value: totalActive, color: '#10b981' },
    { name: 'Deprecated', value: totalDep, color: '#f97316' },
    { name: 'EOL', value: totalEOL, color: '#ef4444' },
  ].filter(d => d.value > 0);

  // EOL risk = categories with >30% depreciation rate
  const eolRisk = dep.map(d => ({
    name: d.category?.replace('_',' '),
    total: +d.total || 0,
    deprecated: +d.depreciated || 0,
    rate: d.total > 0 ? Math.round(((+d.depreciated||0) / +d.total) * 100) : 0,
    value: +d.total_purchase_value || 0,
  })).filter(d => d.total > 0).sort((a,b) => b.rate - a.rate);

  // Category distribution by count (for utilization)
  const catData = (sum?.assetsByCategory || []).reduce((acc, r) => {
    const existing = acc.find(a => a.name === r.category?.replace('_',' '));
    if (existing) existing[r.status] = +r.count;
    else acc.push({ name: r.category?.replace('_',' '), [r.status]: +r.count });
    return acc;
  }, []);

  return (
    <>
    <div className="space-y-4">
      <PageHeader title="Reports & Analytics">
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && (
            <CompanyDeptFilter
              companyId={filterCompany}
              deptId={filterDept}
              onChange={(c, d) => { setFilterCompany(c); setFilterDept(d); }}
            />
          )}
          <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t)=>{setDateFrom(f);setDateTo(t);}}/>
          <button onClick={load} disabled={loading} className="btn-primary text-xs px-3 py-1.5">
            <RefreshCw size={12} className={loading?'animate-spin':''}/>&nbsp;{loading?'Loading...':'Apply'}
          </button>
        </div>
      </PageHeader>

      {/* Tab bar — scrollable when tabs overflow */}
      <div className="overflow-x-auto pb-1" style={{scrollbarWidth:'thin',scrollbarColor:'rgba(255,255,255,0.1) transparent'}}>
      <div className="flex gap-1 p-1 rounded-xl" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)',width:'max-content',minWidth:'100%'}}>
        {tabs.map(([key,label,icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
            style={{background:tab===key?'rgba(59,130,246,0.2)':'transparent',color:tab===key?'#60a5fa':'#475569',border:tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent'}}>
            {icon}{label}
          </button>
        ))}
      </div>
      </div>

      {/* DEPRECIATION TAB */}
      {tab === 'depreciation' && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            {[['Active',totalActive,'#10b981'],['Deprecated',totalDep,'#f97316'],['EOL/Broken',totalEOL,'#ef4444']].map(([label,val,color]) => (
              <Card key={label}>
                <div className="text-3xl font-bold mb-1" style={{color}}>{val}</div>
                <div className="text-xs font-medium" style={{color:'#475569'}}>{label} Assets</div>
                {totalAssets > 0 && <div className="text-xs mt-1" style={{color:'#1e3a5f'}}>{Math.round((val/dep.reduce((s,d)=>s+(+d.total||0),0))*100)||0}% of total</div>}
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stacked bar by category */}
            <Card>
              <STitle>Depreciation Status by Category</STitle>
              {depData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={depData} margin={{left:-15,right:5}}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="name" tick={ax} />
                    <YAxis tick={ax} />
                    <Tooltip content={<DarkTooltip />} />
                    <Legend wrapperStyle={{color:'#475569',fontSize:11}} />
                    <Bar dataKey="Active" fill="#10b981" stackId="s" />
                    <Bar dataKey="Deprecated" fill="#f97316" stackId="s" />
                    <Bar dataKey="EOL" fill="#ef4444" stackId="s" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-48 flex items-center justify-center" style={{color:'#1e3a5f'}}>No data</div>}
            </Card>

            {/* Donut */}
            <Card>
              <STitle>Overall Status Distribution</STitle>
              {statusPie.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={statusPie} dataKey="value" cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={4}
                      label={({name,percent}) => `${name} ${(percent*100).toFixed(0)}%`}
                      labelLine={{stroke:'#1e3a5f',strokeWidth:1}}>
                      {statusPie.map((d,i) => <Cell key={i} fill={d.color} stroke="transparent"/>)}
                    </Pie>
                    <Tooltip content={<DarkTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-48 flex items-center justify-center" style={{color:'#1e3a5f'}}>No data</div>}
            </Card>
          </div>

          {/* Detail table */}
          <Card>
            <STitle>Category Breakdown</STitle>
            <div className="overflow-x-auto">
              <table style={{width:'100%',fontSize:13,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                  {['Category','Total','Active','Deprecated','Dep Rate','Purchase Value'].map(h => (
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600,color:'#334155',textTransform:'uppercase',letterSpacing:'0.05em'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {dep.map((d, i) => {
                    const rate = d.total > 0 ? Math.round(((+d.depreciated||0)/(+d.total))*100) : 0;
                    return (
                      <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer'}}
                        onClick={() => openAssetPopup(`${d.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())} — All Assets (${d.total})`, { category: d.category || undefined, limit: 50 })}
                        onMouseEnter={e => e.currentTarget.style.background='rgba(59,130,246,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <td style={{padding:'9px 12px',color:'#94a3b8',fontWeight:500,textTransform:'capitalize'}}>{d.category?.replace('_',' ')}</td>
                        <td style={{padding:'9px 12px',color:'#60a5fa',fontWeight:700,textDecoration:'underline dotted'}}>{d.total}</td>
                        <td style={{padding:'9px 12px',color:'#34d399'}}>{d.active}</td>
                        <td style={{padding:'9px 12px',color:'#fb923c',cursor:'pointer'}} onClick={e=>{e.stopPropagation();openAssetPopup(`Deprecated: ${d.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}`,{category:d.category,depreciation_status:'depreciated',limit:50});}}>{d.depreciated||0}</td>
                        <td style={{padding:'9px 12px'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <div style={{width:60,height:6,borderRadius:3,background:'rgba(255,255,255,0.06)',overflow:'hidden'}}>
                              <div style={{width:`${rate}%`,height:'100%',background:rate>70?'#ef4444':rate>40?'#f97316':'#10b981',borderRadius:3}}/>
                            </div>
                            <span style={{color:rate>70?'#f87171':rate>40?'#fb923c':'#34d399',fontSize:12,fontWeight:600}}>{rate}%</span>
                          </div>
                        </td>
                        <td style={{padding:'9px 12px',color:'#60a5fa',fontSize:12}}>{d.total_purchase_value ? `LKR ${Number(d.total_purchase_value).toLocaleString()}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* EOL ANALYSIS TAB */}
      {tab === 'eol' && (
        <div className="space-y-4">
          <div className="glass rounded-2xl p-4 flex items-start gap-3" style={{border:'1px solid rgba(249,115,22,0.25)',background:'rgba(249,115,22,0.04)'}}>
            <AlertTriangle size={18} style={{color:'#fb923c',flexShrink:0,marginTop:1}}/>
            <div className="flex-1">
              <div className="font-semibold text-sm" style={{color:'#fb923c'}}>End-of-Life Risk Assessment</div>
              <div className="text-xs mt-0.5" style={{color:'#94a3b8'}}>
                An asset is at end of life once it is flagged EOL, or has passed its EOL date / depreciation end date
                (falling back to <strong style={{color:'#fb923c'}}>4 years</strong> from purchase where no date is recorded).
                Assets already derecognized, disposed or donated are excluded.
              </div>
            </div>
            <button onClick={printEol} disabled={!eolSummary.count}
              className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap"
              style={{opacity: eolSummary.count ? 1 : 0.45, cursor: eolSummary.count ? 'pointer' : 'not-allowed'}}
              title={eolSummary.count ? 'Open a printable A4 report of every EOL asset' : 'No EOL assets to print'}>
              <Printer size={12}/>&nbsp;Print EOL Report
            </button>
          </div>

          {/* EOL summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ['Assets at EOL', eolSummary.count, '#f87171'],
              ['Still Allocated', eolSummary.allocated, '#fb923c'],
              ['Original Cost', fmtLKR(eolSummary.total_cost), '#60a5fa'],
              ['Est. Replacement (+10%)', fmtLKR((eolSummary.total_cost || 0) * 1.1), '#c084fc'],
            ].map(([label, value, color]) => (
              <Card key={label}>
                <div className="text-xs" style={{color:'#475569'}}>{label}</div>
                <div className="text-xl font-bold mt-1" style={{color}}>{value}</div>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <STitle color="#fb923c">Depreciation Rate by Category</STitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={eolRisk} layout="vertical" margin={{left:0,right:20}}>
                  <CartesianGrid {...GRID} horizontal={false} />
                  <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={ax} />
                  <YAxis type="category" dataKey="name" tick={ax} width={80} />
                  <Tooltip content={<DarkTooltip />} formatter={v=>`${v}%`} />
                  <Bar dataKey="rate" name="Dep Rate %" radius={[0,4,4,0]}>
                    {eolRisk.map((d,i) => <Cell key={i} fill={d.rate>70?'#ef4444':d.rate>40?'#f97316':'#10b981'}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <STitle color="#fb923c">EOL Risk Summary</STitle>
              <div className="space-y-2 mt-2">
                {eolRisk.map((d,i) => (
                  <div key={i} className="flex items-center gap-3 py-2" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold"
                      style={{background:d.rate>70?'rgba(239,68,68,0.15)':d.rate>40?'rgba(249,115,22,0.15)':'rgba(16,185,129,0.15)',color:d.rate>70?'#f87171':d.rate>40?'#fb923c':'#34d399'}}>
                      {i+1}
                    </div>
                    <div className="flex-1 cursor-pointer" onClick={() => openAssetPopup(`${d.name} — Deprecated Assets`, { category: d.name.toLowerCase().replace(/ /g,'_'), depreciation_status: 'depreciated', limit: 50 })}>
                      <div className="text-sm capitalize font-medium" style={{color:'#e2e8f0'}}>{d.name}</div>
                      <div className="text-xs" style={{color:'#334155'}}>{d.deprecated} of {d.total} deprecated · <span style={{color:'#60a5fa'}}>click to view</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{color:d.rate>70?'#f87171':d.rate>40?'#fb923c':'#34d399'}}>{d.rate}%</div>
                      <div className="text-xs" style={{color:'#1e3a5f'}}>{d.rate>70?'High Risk':d.rate>40?'Medium':'Low'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* EOL asset register — the list the printout renders */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <STitle color="#f87171">End-of-Life Assets ({eolAssets.length})</STitle>
              {eolAssets.length > 0 && (
                <button onClick={printEol} className="btn-ghost text-xs" style={{color:'#60a5fa'}}>
                  <Printer size={11}/>&nbsp;Print
                </button>
              )}
            </div>
            {eolAssets.length === 0 ? (
              <div className="text-center py-8 text-xs" style={{color:'#475569'}}>
                No assets are at end of life for the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                  <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                    {['Asset #','Category','Brand / Model','Co.','Department','Holder','Purchased','EOL Date','Age','Cost','WDV','Status'].map(h => (
                      <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {eolAssets.map((a, i) => (
                      <tr key={a.id || i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#60a5fa',whiteSpace:'nowrap'}}>{a.asset_number}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8'}}>{cap(a.category)}</td>
                        <td style={{padding:'7px 10px',color:'#e2e8f0'}}>{[a.brand,a.model].filter(Boolean).join(' ')||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#475569',fontFamily:'monospace'}}>{a.company_code||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8'}}>{a.department||'—'}</td>
                        <td style={{padding:'7px 10px',color:a.holder_name?'#94a3b8':'#334155'}}>{a.holder_name||'Unassigned'}</td>
                        <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{a.purchased_date||'—'}</td>
                        <td style={{padding:'7px 10px',color:Number(a.days_to_eol)<0?'#f87171':'#fb923c',whiteSpace:'nowrap'}}>{a.eol_date||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#f87171',fontWeight:700,whiteSpace:'nowrap'}}>{a.age_years??'—'}y</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{fmtLKR(a.purchase_price)}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{fmtLKR(a.wdv)}</td>
                        <td style={{padding:'7px 10px'}}><Badge value={a.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* UTILIZATION TAB */}
      {tab === 'utilization' && sum && (
        <div className="space-y-4">
          <Card>
            <STitle>Asset Status by Category</STitle>
            {catData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={catData} margin={{left:-10,right:10}}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="name" tick={ax} />
                  <YAxis tick={ax} />
                  <Tooltip content={<DarkTooltip />} />
                  <Legend wrapperStyle={{color:'#475569',fontSize:11}} />
                  <Bar dataKey="in_use" name="In Use" fill="#3b82f6" stackId="s" />
                  <Bar dataKey="available" name="Available" fill="#10b981" stackId="s" />
                  <Bar dataKey="in_repair" name="In Repair" fill="#f59e0b" stackId="s" />
                  <Bar dataKey="broken" name="Broken" fill="#ef4444" stackId="s" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-64 flex items-center justify-center" style={{color:'#1e3a5f'}}>No data</div>}
          </Card>
        </div>
      )}

      {/* HANDOVER HISTORY TAB */}
      {tab === 'handovers' && sum && (
        <Card>
          <STitle>Recent Asset Handovers — Last 30 Days</STitle>
          {sum.recentHandovers?.length > 0 ? (
            <div className="overflow-x-auto">
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Asset #', 'Brand / Model', 'Category', 'Handed To', 'Handover Date', 'Purchased', 'Returned'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#334155', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sum.recentHandovers.map((h, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#60a5fa' }}>{h.asset_number}</td>
                      <td style={{ padding: '9px 12px', color: '#e2e8f0' }}>{h.brand} {h.model}</td>
                      <td style={{ padding: '9px 12px', color: '#475569', textTransform: 'capitalize' }}>{h.category?.replace('_', ' ')}</td>
                      <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{h.employee_name}</td>
                      <td style={{ padding: '9px 12px', color: '#34d399' }}>{h.assigned_date}</td>
                      <td style={{ padding: '9px 12px', color: '#475569' }}>{h.purchased_date || '—'}</td>
                      <td style={{ padding: '9px 12px', color: h.returned_date ? '#f59e0b' : '#1e3a5f' }}>{h.returned_date || 'Still holding'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="flex items-center justify-center h-32 text-sm" style={{ color: '#1e3a5f' }}>No handovers in the last 30 days</div>}
        </Card>
      )}

      {/* WDV SCHEDULE TAB */}
      {tab === 'wdv' && (
        <div className="space-y-4">
          {/* Summary cards */}
          {(() => {
            const total = wdvData.length;
            const totalCost = wdvData.reduce((s,r) => s + parseFloat(r.purchase_price||0), 0);
            const totalWDV = wdvData.reduce((s,r) => s + parseFloat(r.wdv||0), 0);
            const totalAccDep = wdvData.reduce((s,r) => s + parseFloat(r.accumulated_depreciation||0), 0);
            const fullyDep = wdvData.filter(r => parseFloat(r.wdv||0) === 0).length;
            return (
              <div className="grid grid-cols-4 gap-4">
                {[
                  ['Total Assets', total, '#60a5fa'],
                  ['Total Book Cost', fmtLKR(totalCost), '#818cf8'],
                  ['Total WDV (Now)', fmtLKR(totalWDV), '#34d399'],
                  ['Fully Depreciated', fullyDep, '#ef4444'],
                ].map(([label,val,color]) => (
                  <Card key={label}>
                    <div className="text-2xl font-bold mb-1" style={{color}}>{val}</div>
                    <div className="text-xs font-medium" style={{color:'#475569'}}>{label}</div>
                    {label === 'Total WDV (Now)' && totalCost > 0 && (
                      <div className="text-xs mt-1" style={{color:'#1e3a5f'}}>{Math.round((totalWDV/totalCost)*100)}% of cost retained</div>
                    )}
                    {label === 'Total Book Cost' && (
                      <div className="text-xs mt-1" style={{color:'#1e3a5f'}}>Acc. Dep: {fmtLKR(totalAccDep)}</div>
                    )}
                  </Card>
                ))}
              </div>
            );
          })()}
          <Card>
            <STitle>Written Down Value Schedule — All Assets (4-Year Straight-Line)</STitle>
            <div className="overflow-x-auto">
              <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.02)'}}>
                  {['Asset #','Brand / Model','Category','Co.','Purchased','Cost (LKR)','Annual Dep','Acc. Dep','WDV (LKR)','Age','Gain/Loss','Type','Approved By'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {wdvData.length === 0 ? (
                    <tr><td colSpan={12} style={{padding:40,textAlign:'center',color:'#1e3a5f'}}>No data — apply date filter and click Apply</td></tr>
                  ) : wdvData.map((r,i) => {
                    const annDep = r.purchase_price ? (parseFloat(r.purchase_price) / 4) : null;
                    const wdv = parseFloat(r.wdv||0);
                    return (
                      <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)',opacity: r.derecognized ? 0.65 : 1}}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#60a5fa',whiteSpace:'nowrap'}}>{r.asset_number}</td>
                        <td style={{padding:'7px 10px',color:'#e2e8f0',whiteSpace:'nowrap'}}>{r.brand} {r.model}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8',textTransform:'capitalize',whiteSpace:'nowrap'}}>{r.category?.replace(/_/g,' ')}</td>
                        <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#334155'}}>{r.company_code}</td>
                        <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{r.purchased_date}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{fmtLKR(r.purchase_price)}</td>
                        <td style={{padding:'7px 10px',color:'#818cf8',whiteSpace:'nowrap'}}>{fmtLKR(annDep)}</td>
                        <td style={{padding:'7px 10px',color:'#f97316',whiteSpace:'nowrap'}}>{fmtLKR(r.accumulated_depreciation)}</td>
                        <td style={{padding:'7px 10px',whiteSpace:'nowrap',fontWeight:700,color: wdv === 0 ? '#ef4444' : '#34d399'}}>{fmtLKR(r.wdv)}</td>
                        <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{r.age_years ? `${parseFloat(r.age_years).toFixed(1)}y` : '—'}</td>
                        <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{r.derecognized && r.gain_loss != null ? fmtGainLoss(r.gain_loss) : <span style={{color:'#1e3a5f'}}>—</span>}</td>
                        <td style={{padding:'7px 10px',color:'#475569',textTransform:'capitalize',whiteSpace:'nowrap'}}>{r.derecognition_type?.replace('_',' ')||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{r.derecognition_approved_by||'—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ASSET DISPOSAL TAB */}
      {tab === 'disposal' && (
        <div className="space-y-4">
          {/* Summary cards */}
          {disposal.summary && (
            <div className="grid grid-cols-4 gap-4">
              {[
                ['Total Disposed', disposal.summary.total_disposed, '#f87171'],
                ['Total Sale Value', fmtLKR(disposal.summary.total_sale_value), '#34d399'],
                ['Net Gain / Loss', null, disposal.summary.total_gain_loss >= 0 ? '#34d399' : '#f87171'],
                ['Acc. Dep Written Off', fmtLKR(disposal.summary.total_accumulated_dep), '#f97316'],
              ].map(([label,val,color],idx) => (
                <Card key={label}>
                  <div className="text-2xl font-bold mb-1" style={{color}}>
                    {idx === 2 ? fmtGainLoss(disposal.summary.total_gain_loss) : val}
                  </div>
                  <div className="text-xs font-medium" style={{color:'#475569'}}>{label}</div>
                  {idx === 0 && (
                    <div className="text-xs mt-1" style={{color:'#334155'}}>
                      Sold: {disposal.summary.sold||0} · Donated: {disposal.summary.donated||0} · Scrapped: {disposal.summary.scrapped||0}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {/* By-type chart */}
          {disposal.byType.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <STitle>Disposal by Type</STitle>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={disposal.byType} dataKey="count" nameKey="type" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}
                      label={({type,count}) => `${type?.replace('_',' ')} (${count})`}
                      labelLine={{stroke:'#1e3a5f',strokeWidth:1}}>
                      {disposal.byType.map((_,i) => <Cell key={i} fill={PIE_C[i % PIE_C.length]} stroke="transparent"/>)}
                    </Pie>
                    <Tooltip content={<DarkTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
              <Card>
                <STitle>Gain/Loss by Disposal Type</STitle>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={disposal.byType.map(d => ({
                    name: d.type?.replace('_',' ') || 'unknown',
                    'Gain/Loss': parseFloat(d.gain_loss||0),
                    'Sale Value': parseFloat(d.sale_value||0),
                  }))} margin={{left:-10}}>
                    <CartesianGrid {...GRID}/>
                    <XAxis dataKey="name" tick={ax}/>
                    <YAxis tick={ax} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}/>
                    <Tooltip content={<DarkTooltip/>}/>
                    <Legend wrapperStyle={{color:'#475569',fontSize:11}}/>
                    <Bar dataKey="Sale Value" fill="#3b82f6"/>
                    <Bar dataKey="Gain/Loss" radius={[4,4,0,0]}>
                      {disposal.byType.map((d,i) => <Cell key={i} fill={parseFloat(d.gain_loss||0) >= 0 ? '#10b981' : '#ef4444'}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}

          <Card>
            <STitle color="#f87171">Derecognized Assets — Detail</STitle>
            <div className="overflow-x-auto">
              <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.02)'}}>
                  {['Asset #','Brand / Model','Category','Co.','Purchased','Cost (LKR)','Disposal Date','Type','WDV at Disposal','Sale Value','Acc. Dep','Gain / Loss','Approved By'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {disposal.detail.length === 0 ? (
                    <tr><td colSpan={12} style={{padding:40,textAlign:'center',color:'#1e3a5f'}}>No derecognized assets found in the selected period</td></tr>
                  ) : disposal.detail.map((r,i) => (
                    <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.05)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#60a5fa',whiteSpace:'nowrap'}}>{r.asset_number}</td>
                      <td style={{padding:'7px 10px',color:'#e2e8f0',whiteSpace:'nowrap'}}>{r.brand} {r.model}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',textTransform:'capitalize',whiteSpace:'nowrap'}}>{r.category?.replace(/_/g,' ')}</td>
                      <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#334155'}}>{r.company_code}</td>
                      <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{r.purchased_date}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{fmtLKR(r.purchase_price)}</td>
                      <td style={{padding:'7px 10px',color:'#f87171',whiteSpace:'nowrap'}}>{r.derecognition_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#818cf8',textTransform:'capitalize',whiteSpace:'nowrap'}}>{r.derecognition_type?.replace('_',' ')||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#34d399',whiteSpace:'nowrap'}}>{fmtLKR(r.wdv_at_disposal)}</td>
                      <td style={{padding:'7px 10px',color:'#60a5fa',whiteSpace:'nowrap'}}>{r.sale_value ? fmtLKR(r.sale_value) : '—'}</td>
                      <td style={{padding:'7px 10px',color:'#f97316',whiteSpace:'nowrap'}}>{fmtLKR(r.accumulated_dep)}</td>
                      <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{fmtGainLoss(r.gain_loss)}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{r.derecognition_approved_by||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ACCESSORY DISPOSAL TAB */}
      {tab === 'acc_disposal' && (
        <div className="space-y-4">
          {(() => {
            const total = accDisposal.length;
            const totalSale = accDisposal.reduce((s,r) => s + parseFloat(r.sale_value||0), 0);
            const totalGL = accDisposal.reduce((s,r) => s + parseFloat(r.gain_loss||0), 0);
            return (
              <div className="grid grid-cols-3 gap-4">
                {[
                  ['Total Disposed', total, '#f87171'],
                  ['Total Sale Value', fmtLKR(totalSale), '#34d399'],
                  ['Net Gain / Loss', null, totalGL >= 0 ? '#34d399' : '#f87171'],
                ].map(([label,val,color],idx) => (
                  <Card key={label}>
                    <div className="text-2xl font-bold mb-1" style={{color}}>{idx === 2 ? fmtGainLoss(totalGL) : val}</div>
                    <div className="text-xs font-medium" style={{color:'#475569'}}>{label}</div>
                  </Card>
                ))}
              </div>
            );
          })()}
          <Card>
            <STitle color="#f87171">Derecognized Accessories — Detail</STitle>
            <div className="overflow-x-auto">
              <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.02)'}}>
                  {['ACC #','Name','Category','Brand/Model','Purchased','Cost (LKR)','Disposal Date','Type','WDV at Disposal','Sale Value','Gain / Loss'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {accDisposal.length === 0 ? (
                    <tr><td colSpan={11} style={{padding:40,textAlign:'center',color:'#1e3a5f'}}>No derecognized accessories found in the selected period</td></tr>
                  ) : accDisposal.map((r,i) => (
                    <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.05)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#60a5fa',whiteSpace:'nowrap'}}>{r.accessory_number||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#e2e8f0',whiteSpace:'nowrap'}}>{r.name}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{r.category||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{r.brand} {r.model}</td>
                      <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{r.purchased_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{fmtLKR(r.purchase_price)}</td>
                      <td style={{padding:'7px 10px',color:'#f87171',whiteSpace:'nowrap'}}>{r.derecognition_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#818cf8',textTransform:'capitalize',whiteSpace:'nowrap'}}>{r.derecognition_type?.replace('_',' ')||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#34d399',whiteSpace:'nowrap'}}>{fmtLKR(r.wdv_at_disposal)}</td>
                      <td style={{padding:'7px 10px',color:'#60a5fa',whiteSpace:'nowrap'}}>{r.sale_value ? fmtLKR(r.sale_value) : '—'}</td>
                      <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{fmtGainLoss(r.gain_loss)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* TOP HOLDERS TAB */}
      {tab === 'tickets' && (
        <Card>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <STitle>IT Ticket Report {dateFrom || dateTo ? `(${dateFrom||'start'} → ${dateTo||'now'})` : '(All Time)'}</STitle>
            <div className="flex gap-2 flex-wrap">
              <select value={ticketStatus} onChange={e=>setTicketStatus(e.target.value)} className="input-dark text-xs py-1" style={{width:140}}>
                <option value="">All Statuses</option>
                {['open','in_progress','on_hold','resolved','closed'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </select>
              <select value={ticketCategory} onChange={e=>setTicketCategory(e.target.value)} className="input-dark text-xs py-1" style={{width:160}}>
                <option value="">All Categories</option>
                {['hardware_issue','software_issue','network_issue','email_issue','access_request','printer_issue','phone_issue','security_incident','new_setup','data_recovery','other'].map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
              </select>
              <button onClick={load} className="btn-primary text-xs px-3 py-1">Apply</button>
            </div>
          </div>
          {/* Summary pills */}
          <div className="flex gap-3 mb-4 flex-wrap">
            {[
              { label: 'Total', val: tickets.summary?.total, color: '#818cf8' },
              { label: 'Open', val: tickets.summary?.open_count, color: '#f59e0b' },
              { label: 'Resolved/Closed', val: tickets.summary?.resolved_count, color: '#10b981' },
              { label: 'Avg Resolution', val: tickets.summary?.avg_resolution_hours ? `${tickets.summary.avg_resolution_hours}h` : '—', color: '#06b6d4' },
            ].map((s,i) => (
              <div key={i} className="px-3 py-2 rounded-xl text-xs font-medium"
                style={{background:`${s.color}12`,border:`1px solid ${s.color}25`,color:s.color}}>
                {s.label}: <span className="font-bold">{s.val||0}</span>
              </div>
            ))}
          </div>
          {tickets.tickets?.length === 0 ? (
            <div className="flex items-center justify-center h-32" style={{color:'#1e3a5f'}}>No tickets found for the selected filters</div>
          ) : (
            <div className="overflow-x-auto">
              <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.03)'}}>
                  {['Ticket #','Title','Category','Priority','Status','Reporter','Assigned To','Reported','Resolved','Hours'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:10,color:'#475569',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {tickets.tickets.map((t,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.05)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{padding:'7px 10px',fontFamily:'monospace',color:'#60a5fa',whiteSpace:'nowrap'}}>{t.ticket_number}</td>
                      <td style={{padding:'7px 10px',color:'#e2e8f0',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.title}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',textTransform:'capitalize',whiteSpace:'nowrap'}}>{t.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                      <td style={{padding:'7px 10px'}}><Badge value={t.priority}/></td>
                      <td style={{padding:'7px 10px'}}><Badge value={t.status}/></td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{t.reporter||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',whiteSpace:'nowrap'}}>{t.assigned_to||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{t.reported_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#475569',whiteSpace:'nowrap'}}>{t.resolved_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:parseFloat(t.resolution_hours)>24?'#f87171':'#94a3b8',whiteSpace:'nowrap'}}>{t.resolution_hours?`${t.resolution_hours}h`:'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'holders' && sum && (
        <Card>
          <STitle>Top 10 Asset Holders</STitle>
          {sum.topHolders?.length > 0 ? (
            <div className="space-y-3">
              {sum.topHolders.map((h,i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{background:i<3?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.05)',color:i<3?'#60a5fa':'#475569',border:`1px solid ${i<3?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.08)'}`}}>
                    {i+1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium" style={{color:'#e2e8f0'}}>{h.full_name}</span>
                      <span className="text-sm font-bold" style={{color:'#60a5fa'}}>{h.asset_count}</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{background:'rgba(255,255,255,0.06)'}}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{width:`${(h.asset_count/sum.topHolders[0].asset_count)*100}%`,background:'linear-gradient(90deg,#3b82f6,#8b5cf6)'}}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="flex items-center justify-center h-48" style={{color:'#1e3a5f'}}>No data</div>}
        </Card>
      )}
    </div>
      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(4,8,16,0.9)', backdropFilter: 'blur(10px)' }}>
          <div className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden"
            style={{ background: '#0d1528', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.8)' }}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div className="font-bold" style={{ color: '#e2e8f0', fontSize: 15 }}>{popup.title}</div>
                <div style={{ color: '#334155', fontSize: 11, marginTop: 2 }}>
                  {popupLoading ? 'Loading...' : `${popupData.length} assets`}
                </div>
              </div>
              <button onClick={() => { setPopup(null); setPopupData([]); }}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 8px', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={15}/>
              </button>
            </div>
            <div className="overflow-y-auto flex-1" style={{ padding: '4px 8px 8px' }}>
              {popupLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, gap: 10, color: '#334155' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }}/>
                  Loading records...
                </div>
              ) : popupData.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, color: '#1e3a5f' }}>No records found</div>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}>
                      {['Asset #','Brand / Model','Category','Holder','Purchased','Dep. Status','Status'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {popupData.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#60a5fa', whiteSpace: 'nowrap' }}>{a.asset_number}</td>
                        <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{a.brand} {a.model}</td>
                        <td style={{ padding: '8px 12px', color: '#475569', textTransform: 'capitalize' }}>{a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{a.holder_name||'—'}</td>
                        <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{a.purchased_date||'—'}</td>
                        <td style={{ padding: '8px 12px' }}><Badge value={a.depreciation_status}/></td>
                        <td style={{ padding: '8px 12px' }}><Badge value={a.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── ACCESSORIES TAB ── */}
      {tab === 'accessories' && (
        <div className="space-y-4">
          {accReport ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[['Total',accReport.summary?.total,'#c084fc'],['In Use',accReport.summary?.in_use,'#3b82f6'],['Available',accReport.summary?.available,'#10b981'],['Disposed',accReport.summary?.disposed,'#f87171']].map(([l,v,c])=>(
                  <Card key={l}><div className="text-3xl font-bold mb-1" style={{color:c}}>{v||0}</div><div className="text-xs" style={{color:'#475569'}}>{l} Accessories</div></Card>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><STitle>By Category</STitle>
                  <div className="overflow-x-auto"><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                    <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                      {['Category','Total','In Use','Available','Disposed'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{(accReport.categoryBreakdown||[]).map((r,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <td style={{padding:'7px 10px',color:'#e2e8f0',fontWeight:500}}>{r.category||'Unknown'}</td>
                        <td style={{padding:'7px 10px',color:'#c084fc',fontWeight:700}}>{r.total}</td>
                        <td style={{padding:'7px 10px',color:'#60a5fa'}}>{r.in_use||0}</td>
                        <td style={{padding:'7px 10px',color:'#34d399'}}>{r.available||0}</td>
                        <td style={{padding:'7px 10px',color:'#f87171'}}>{r.disposed||0}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </Card>
                <Card><STitle>Brand Distribution</STitle>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={(accReport.brandDist||[]).slice(0,8).map(b=>({name:b.brand,count:+b.count}))} layout="vertical" margin={{left:0,right:5}}>
                      <CartesianGrid {...GRID} horizontal={false}/>
                      <XAxis type="number" tick={ax}/><YAxis type="category" dataKey="name" tick={{...ax,fontSize:10}} width={80}/>
                      <Tooltip content={<DarkTooltip/>}/>
                      <Bar dataKey="count" name="Count" fill="#c084fc" radius={[0,4,4,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
              {(accReport.warrantyExpiring||[]).length > 0 && (
                <Card><STitle color="#f59e0b">Warranty Expiring in 90 Days</STitle>
                  <div className="overflow-x-auto"><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                    <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                      {['Accessory #','Name','Brand','Category','Expiry','Holder'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{(accReport.warrantyExpiring||[]).map((r,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <td style={{padding:'7px 10px',color:'#60a5fa',fontFamily:'monospace'}}>{r.accessory_number||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#e2e8f0'}}>{r.name||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8'}}>{r.brand||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#475569'}}>{r.category||'—'}</td>
                        <td style={{padding:'7px 10px',color:'#f59e0b',fontWeight:600}}>{r.warranty_expiry_date}</td>
                        <td style={{padding:'7px 10px',color:'#94a3b8'}}>{r.holder_name||'—'}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </Card>
              )}
            </>
          ) : <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{color:'#1e3a5f'}}>No accessories data</div>}
        </div>
      )}

      {/* ── TONER TAB ── */}
      {tab === 'toner' && (
        <div className="space-y-4">
          {tonerReport ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                {[['Types in Stock',tonerReport.summary?.total_types,'#f59e0b'],['Total Units',tonerReport.summary?.total_stock,'#3b82f6'],['Total Value',tonerReport.summary?.total_value?`LKR ${Number(tonerReport.summary.total_value).toLocaleString()}`:'—','#10b981']].map(([l,v,c])=>(
                  <Card key={l}><div className="text-3xl font-bold mb-1" style={{color:c}}>{v??'—'}</div><div className="text-xs" style={{color:'#475569'}}>{l}</div></Card>
                ))}
              </div>
              <Card><STitle>Stock Levels</STitle>
                <div className="overflow-x-auto"><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                  <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                    {['Toner Model','Vendor','In Stock','Reorder At','Total Issued','Unit Price','Status'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{(tonerReport.stockLevels||[]).map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <td style={{padding:'7px 10px',color:'#e2e8f0',fontWeight:500}}>{r.toner_model}</td>
                      <td style={{padding:'7px 10px',color:'#475569'}}>{r.vendor||'—'}</td>
                      <td style={{padding:'7px 10px',color:r.low_stock?'#f87171':'#34d399',fontWeight:700}}>{r.quantity_in_stock}</td>
                      <td style={{padding:'7px 10px',color:'#475569'}}>{r.reorder_level}</td>
                      <td style={{padding:'7px 10px',color:'#f97316'}}>{r.total_issued||0}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8'}}>{r.unit_price?`LKR ${Number(r.unit_price).toLocaleString()}`:'—'}</td>
                      <td style={{padding:'7px 10px'}}><span style={{color:r.low_stock?'#f87171':'#34d399',fontSize:11}}>{r.low_stock?'Low Stock':'OK'}</span></td>
                    </tr>
                  ))}</tbody>
                </table></div>
              </Card>
            </>
          ) : <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{color:'#1e3a5f'}}>No toner data</div>}
        </div>
      )}

      {/* ── LICENSES TAB ── */}
      {tab === 'licenses' && (
        <div className="space-y-4">
          {licenseReport ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[['Total',licenseReport.summary?.total,'#60a5fa'],['In Use',licenseReport.summary?.in_use,'#10b981'],['Expiring (90d)',licenseReport.summary?.expiring_90d,'#f59e0b'],['Expired',licenseReport.summary?.expired,'#f87171']].map(([l,v,c])=>(
                  <Card key={l}><div className="text-3xl font-bold mb-1" style={{color:c}}>{v||0}</div><div className="text-xs" style={{color:'#475569'}}>{l}</div></Card>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><STitle>By License Type</STitle>
                  <div className="space-y-2">
                    {(licenseReport.byType||[]).map((r,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs py-1" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{color:'#e2e8f0',textTransform:'capitalize'}}>{r.license_type?.replace(/_/g,' ')}</span>
                        <span style={{color:'#60a5fa',fontWeight:700}}>{r.count}</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card><STitle>Expiry Timeline (Next 12 Months)</STitle>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={(licenseReport.expiryTimeline||[]).map(r=>({name:r.month,count:+r.count}))} margin={{left:-15}}>
                      <CartesianGrid {...GRID}/><XAxis dataKey="name" tick={{...ax,fontSize:9}}/><YAxis tick={ax}/>
                      <Tooltip content={<DarkTooltip/>}/>
                      <Bar dataKey="count" name="Expiring" fill="#f59e0b" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
              <Card><STitle>All Licenses</STitle>
                <div className="overflow-x-auto"><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                  <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                    {['License Key','Type','Status','Vendor','Expiry','Asset'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{(licenseReport.detail||[]).slice(0,100).map((r,i)=>{
                    const expired = r.expiry_date && new Date(r.expiry_date) < new Date();
                    return (<tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <td style={{padding:'7px 10px',color:'#60a5fa',fontFamily:'monospace',fontSize:11}}>{r.license_key?.slice(0,20)}{r.license_key?.length>20?'…':''}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8',textTransform:'capitalize'}}>{r.license_type}</td>
                      <td style={{padding:'7px 10px'}}><Badge value={r.status}/></td>
                      <td style={{padding:'7px 10px',color:'#475569'}}>{r.vendor||'—'}</td>
                      <td style={{padding:'7px 10px',color:expired?'#f87171':'#94a3b8'}}>{r.expiry_date||'No expiry'}</td>
                      <td style={{padding:'7px 10px',color:'#60a5fa',fontFamily:'monospace'}}>{r.asset_number||'—'}</td>
                    </tr>);
                  })}</tbody>
                </table></div>
              </Card>
            </>
          ) : <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{color:'#1e3a5f'}}>No license data</div>}
        </div>
      )}

      {/* ── INSURANCE TAB ── */}
      {tab === 'insurance_rep' && (
        <div className="space-y-4">
          {insuranceReport ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[['Total Policies',insuranceReport.summary?.total,'#3b82f6'],['Active',insuranceReport.summary?.active,'#10b981'],['Expiring (30d)',insuranceReport.summary?.expiring_30d,'#f59e0b'],['Expired',insuranceReport.summary?.expired,'#f87171']].map(([l,v,c])=>(
                  <Card key={l}><div className="text-3xl font-bold mb-1" style={{color:c}}>{v||0}</div><div className="text-xs" style={{color:'#475569'}}>{l}</div></Card>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><STitle>Claims Summary</STitle>
                  <div className="space-y-2">
                    {[['Total Claims',insuranceReport.claimSummary?.total,'#94a3b8'],['Pending',insuranceReport.claimSummary?.pending,'#f59e0b'],['Approved',insuranceReport.claimSummary?.approved,'#10b981'],['Rejected',insuranceReport.claimSummary?.rejected,'#f87171']].map(([l,v,c])=>(
                      <div key={l} className="flex items-center justify-between text-sm py-1.5" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{color:'#94a3b8'}}>{l}</span>
                        <span style={{color:c,fontWeight:700}}>{v||0}</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card><STitle>Coverage by Company</STitle>
                  <div className="space-y-2">
                    {(insuranceReport.byCompany||[]).map((r,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs py-1" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{color:'#e2e8f0'}}>{r.company} ({r.code})</span>
                        <span style={{color:'#3b82f6',fontWeight:600}}>{r.count} policies</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <Card><STitle>All Insurance Policies</STitle>
                <div className="overflow-x-auto"><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                  <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                    {['Policy #','Insurer','Asset','Coverage','Start','End','Status'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,color:'#334155',fontWeight:700,textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>{(insuranceReport.detail||[]).slice(0,100).map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <td style={{padding:'7px 10px',color:'#60a5fa',fontFamily:'monospace'}}>{r.policy_number||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#94a3b8'}}>{r.insurer||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#60a5fa',fontFamily:'monospace'}}>{r.asset_number||'—'}</td>
                      <td style={{padding:'7px 10px',color:'#10b981'}}>{r.coverage_amount?`LKR ${Number(r.coverage_amount).toLocaleString()}`:'—'}</td>
                      <td style={{padding:'7px 10px',color:'#475569'}}>{r.start_date||'—'}</td>
                      <td style={{padding:'7px 10px',color:r.status==='expired'?'#f87171':'#94a3b8'}}>{r.end_date||'—'}</td>
                      <td style={{padding:'7px 10px'}}><span style={{color:r.status==='active'?'#34d399':'#f87171',fontWeight:600,textTransform:'capitalize'}}>{r.status}</span></td>
                    </tr>
                  ))}</tbody>
                </table></div>
              </Card>
            </>
          ) : <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{color:'#1e3a5f'}}>No insurance data</div>}
        </div>
      )}

      {/* ── OPERATIONS TAB ── */}
      {tab === 'operations' && (
        <div className="space-y-4">
          {opsReport ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[['Total Repairs',opsReport.repairSummary?.total,'#8b5cf6'],['Damages',opsReport.damageSummary?.total,'#f87171'],['Donations',opsReport.donationSummary?.total,'#10b981'],['Total Tickets',opsReport.ticketSummary?.total,'#f59e0b']].map(([l,v,c])=>(
                  <Card key={l}><div className="text-3xl font-bold mb-1" style={{color:c}}>{v||0}</div><div className="text-xs" style={{color:'#475569'}}>{l}</div></Card>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card><STitle>Repairs by Status</STitle>
                  <div className="space-y-2">
                    {[['Pending',opsReport.repairSummary?.pending,'#f59e0b'],['In Progress',opsReport.repairSummary?.in_progress,'#3b82f6'],['Completed',opsReport.repairSummary?.completed,'#10b981']].map(([l,v,c])=>(
                      <div key={l} className="flex items-center justify-between text-sm py-1.5" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{color:'#94a3b8'}}>{l}</span><span style={{color:c,fontWeight:700}}>{v||0}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm py-1.5">
                      <span style={{color:'#94a3b8'}}>Total Repair Cost</span>
                      <span style={{color:'#f87171',fontWeight:700}}>{opsReport.repairSummary?.total_cost?`LKR ${Number(opsReport.repairSummary.total_cost).toLocaleString()}`:'—'}</span>
                    </div>
                  </div>
                </Card>
                <Card><STitle>Tickets by Category</STitle>
                  <div className="space-y-1">
                    {(opsReport.ticketsByCategory||[]).map((r,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs py-1" style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <span style={{color:'#e2e8f0',textTransform:'capitalize'}}>{r.category?.replace(/_/g,' ')}</span>
                        <span style={{color:'#f59e0b',fontWeight:600}}>{r.count}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          ) : <div className="glass glow-border rounded-2xl p-10 text-center text-sm" style={{color:'#1e3a5f'}}>No operations data</div>}
        </div>
      )}

    </>
  );
}
